import { Injector } from '@angular/core';
import { FormID } from '@finxone-platform/form-action';
import {
  ActionButtonOutcomesType,
  NavigationOptionEnum,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { catchError, of, tap } from 'rxjs';
import { ClearFormDataActionWithId } from '../../../actions/form-submission.action';
import { CreateSavingsAccount, GetAccountRequests } from '../../../actions/savings-account.action';
import { SavingsAccountState } from '../../../state/savings-account.state';
import { CtaButtonSignalService } from '../../../services/cta-button-signal-service/cta-button-signal.service';
import { SavingsAccountService } from '../../../services/savings-account-service/savings-account.service';
import {
  CreateSavingsAccountRequest,
  SavingsAccountInfoTypeResponse,
} from '../../../services/savings-account-service/savings-account.type';
import { WorkflowFormsService } from '../../../services/workflow-forms/workflow-forms.service';
import { FormActionState } from '../../../state/form-submision.state';
import { SavingsProductsState } from '../../../state/savings-products.state';
import { handleRedirection } from '../cta-redirection.utils';

export function openNewSavingsAccount(injector: Injector, attri: UiZoneWidgetAttributeConfig) {
  const store = injector.get(Store);
  const savingsService = injector.get(SavingsAccountService);
  const ctaButtonSignalService = injector.get(CtaButtonSignalService);
  const workflowFormService = injector.get(WorkflowFormsService);
  const openAccountForm = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(FormID.OPEN_NEW_ACCOUNT_FORM),
  );
  const openAccountFormData = openAccountForm?.formData as Record<string, string>;
  const availableProducts = store.selectSnapshot(SavingsProductsState.getAvailableProducts);
  const isValid = workflowFormService.currentPageFormsValid();
  const ctaAction = attri.widgetProperties.buttonActionType as string;
  const baseFields = ['accountType', 'productCode', 'accountName', 'currency'];
  const baseFieldValues: Record<string, string> = {};
  for (const key of baseFields) {
    if (openAccountFormData[key] !== undefined) {
      baseFieldValues[key] = openAccountFormData[key];
    }
  }
  const accountType = availableProducts.find(
    (product) => product.productCode === openAccountFormData['productCode'],
  )?.accountType;

  if (accountType) {
    baseFieldValues['accountType'] = accountType;
  }
  //TODO: When savings accounts can be in more currencies, set currency based on available currency attached to the available product
  baseFieldValues['currency'] = 'GBP';

  const extraAttributes: Record<string, string> = {};
  for (const [key, value] of Object.entries(openAccountFormData)) {
    if (!baseFields.includes(key)) {
      extraAttributes[key] = value;
    }
  }
  const openAccountObject = {
    accountType: accountType,
    productCode: openAccountFormData['productCode'],
    currency: 'GBP',
    extraAttributes: extraAttributes,
  } as CreateSavingsAccountRequest;
  if (isValid) {
    //disable button on press
    ctaButtonSignalService.setSignal({ [ctaAction]: false });

    store
      .dispatch(new CreateSavingsAccount(openAccountObject))
      .pipe(
        tap(() => {
          const latestRequest = store.selectSnapshot(SavingsAccountState.getAccounts)?.latestAccountRequest;
          if (latestRequest) {
            const status: Pick<SavingsAccountInfoTypeResponse, 'status'> = {
              status: latestRequest.status,
            };
            const outcomeType = getActionButtonOutcomeType(status);
            handleRedirection(attri.widgetProperties, injector, outcomeType, true);
          }
          ctaButtonSignalService.setSignal({ [ctaAction]: true });
          store.dispatch(new ClearFormDataActionWithId(FormID.OPEN_NEW_ACCOUNT_FORM));
          store.dispatch(new GetAccountRequests());
        }),
        catchError((error) => {
          console.error('error creating account:', error);
          if (
            attri.widgetProperties?.['actionNavigationConfigurations']?.[
              ActionButtonOutcomesType.ALTERNATE_1
            ] &&
            attri.widgetProperties?.['actionNavigationConfigurations']?.[ActionButtonOutcomesType.ALTERNATE_1]
              .selectedNavigationOption !== NavigationOptionEnum.USE_AS_BLANK
          ) {
            handleRedirection(attri.widgetProperties, injector, ActionButtonOutcomesType.ALTERNATE_1, true);
          }
          ctaButtonSignalService.setSignal({ [ctaAction]: true });
          return of(null);
        }),
      )
      .subscribe();
  } else {
    workflowFormService.markCurrentPageFormsTouched();
  }
}

function getActionButtonOutcomeType(
  accountStatus: Pick<SavingsAccountInfoTypeResponse, 'status'>,
): ActionButtonOutcomesType {
  let outcomeType: ActionButtonOutcomesType;
  switch (accountStatus.status) {
    case 'approved':
      outcomeType = ActionButtonOutcomesType.SUCCESS;
      break;
    case 'rejected':
      outcomeType = ActionButtonOutcomesType.FAILURE;
      break;
    case 'awaiting-approval':
      outcomeType = ActionButtonOutcomesType.PROCESSING;
      break;
    default:
      outcomeType = ActionButtonOutcomesType.FAILURE;
  }
  return outcomeType;
}
